// OptionsMenu: practice toggles and sidebar controls (React version)

import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch, store } from '../store';
import type { RootState } from '../store';
import { setMode, setVoice, toggleHideMyLines, toggleCueMode, toggleStageDirections, toggleMic } from '../store/settingsSlice';
import { setDrillMode, setLineIndex, startPractice } from '../store/practiceSlice';
import { setParsedLines, setScriptMeta, setEditedScriptLines, setRecordedAudio,
         setMyCharacters, setFlowSource } from '../store/scriptSlice';
import { loadScripts, loadPersistedEdits, loadPersistedRecordings,
         loadSelectedCharacters, loadLastMode } from '../lib/persistence';
import { parseScript } from '../services/scriptParser';
import { stopSpeech } from '../services/speech';
import { checkMicPermission } from '../services/mic-stt';
import type { ViewName } from '../app';

// ---------------------------------------------------------------------------
// Utility functions used by other components (read directly from store)
// ---------------------------------------------------------------------------

export function getWorkingLines() {
    const state = store.getState();
    if (state.script.editedScriptLines && state.script.editedScriptLines.length > 0) {
        return state.script.editedScriptLines;
    }
    return state.script.parsedLines;
}

export function getDisplayLinesCount(): number {
    const { removeStageDirections } = store.getState().settings;
    const workingLines = getWorkingLines();
    return removeStageDirections
        ? workingLines.filter(l => !l.isStageDirection).length
        : workingLines.length;
}

export function getCurrentActualIndex(): number | undefined {
    const { practice, settings } = store.getState();
    const workingLines = getWorkingLines();
    if (practice.isDrillingProblems) {
        return practice.problemLineIndices[practice.currentLineIndex];
    }
    if (settings.removeStageDirections) {
        let count = 0;
        for (let i = 0; i < workingLines.length; i++) {
            if (!workingLines[i].isStageDirection) {
                if (count === practice.currentLineIndex) return i;
                count++;
            }
        }
        return undefined;
    }
    return practice.currentLineIndex;
}

function escapeHtml(str: string): string {
    return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function getCueMarkup(dialogue: string, actualIndex: number): string {
    const { revealedCueLineIndices } = store.getState().practice;
    const raw = String(dialogue || '');
    const words = raw.trim().split(/\s+/).filter(Boolean);
    if (revealedCueLineIndices.includes(actualIndex)) {
        return escapeHtml(raw);
    }
    if (words.length === 0) return '';
    if (words.length <= 3) {
        const visible = escapeHtml(words[0]);
        const hidden = words.slice(1).map(() => '<span class="cue-mask-word">____</span>').join(' ');
        return hidden ? `${visible} ${hidden}` : visible;
    }
    const visible = words.slice(0, 3).map(escapeHtml).join(' ');
    const hidden = words.slice(3).map(() => '<span class="cue-mask-word">____</span>').join(' ');
    return `${visible} ${hidden}`;
}

export function revealCueLine(actualIndex: number): void {
    const { cueMode } = store.getState().settings;
    if (!cueMode) return;
    const { revealCueLine: revealAction } = require('../store/practiceSlice');
    store.dispatch(revealAction(actualIndex));
}

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------

interface OptionsMenuProps {
    onNavigate: (view: ViewName) => void;
    visible: boolean;
}

export function OptionsMenu({ onNavigate, visible }: OptionsMenuProps) {
    const dispatch = useAppDispatch();
    const { mode, selectedVoice, hideMyLines, cueMode, removeStageDirections, micEnabled } = useAppSelector(s => s.settings);
    const { problemLines, isPracticing } = useAppSelector(s => s.practice);
    const { currentScriptName } = useAppSelector(s => s.script);
    const [micPermission, setMicPermission] = useState<string>('checking');

    useEffect(() => {
        checkMicPermission().then(setMicPermission);
    }, []);

    // Detect browser for correct instructions
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const permissionInstructions = isSafari
        ? 'Enable mic in Settings → Safari → runlines'
        : 'Click the camera icon in your address bar to allow microphone';

    const problemIndices = Object.keys(problemLines).filter(i => problemLines[Number(i)] > 0);
    const problemCount = problemIndices.length;

    const handleDrillProblemLines = () => {
        const indices = problemIndices.map(Number);
        if (indices.length === 0) return;
        dispatch(setDrillMode({ enabled: true, indices }));
        dispatch(setLineIndex(0));
    };

    return (
        <div className={`side-panel ${visible ? 'visible' : ''}`} id="side-panel">
            <div className="panel-section">
                <div className="panel-title">Current Script</div>
                <select
                    className="script-selector"
                    id="script-selector"
                    value={currentScriptName || ''}
                    onChange={async (e) => {
                        const name = e.target.value;
                        if (!name) return;
                        const saved = loadScripts();
                        const script = saved.find(s => s.name === name);
                        if (!script) return;
                        stopSpeech();
                        dispatch(setScriptMeta({ name: script.name, id: script.id || null }));
                        dispatch(setRecordedAudio(script.recordedAudio || null));
                        dispatch(setEditedScriptLines(null));
                        if (await parseScript(script.text, script.name)) {
                            loadPersistedRecordings();
                            loadPersistedEdits();
                            const validChars = new Set(getWorkingLines().map(l => l.character).filter(Boolean));
                            const restoredChars = loadSelectedCharacters(script, validChars);
                            if (restoredChars.size > 0) {
                                dispatch(setMyCharacters(Array.from(restoredChars)));
                                dispatch(startPractice());
                            } else {
                                onNavigate('character');
                            }
                        }
                    }}
                >
                    <option value="">Select a script...</option>
                    {loadScripts().map((script) => (
                        <option key={script.id || script.name} value={script.name}>
                            {script.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="panel-section">
                <div className="panel-title">Practice Mode</div>
                <div className="toggle-group">
                    <div
                        className={`toggle-option${mode === 'relaxed' ? ' active' : ''}`}
                        id="mode-relaxed"
                        onClick={() => dispatch(setMode('relaxed'))}
                    >
                        Relaxed
                    </div>
                    <div className="toggle-option coming-soon" id="mode-perfect">
                        Word Perfect (Coming soon)
                    </div>
                </div>
                <p className="mode-description" id="mode-description">
                    {mode === 'relaxed'
                        ? 'Listens for key words and meaning. Continues when you finish speaking.'
                        : 'Requires exact word match. Shows corrections and tracks problem lines.'}
                </p>
            </div>

            <div className="panel-section">
                <div className="panel-title">Voice</div>
                <div className="voice-options">
                    <div
                        className={`voice-option${selectedVoice === 'female' ? ' selected' : ''}`}
                        id="voice-female"
                        onClick={() => dispatch(setVoice('female'))}
                    >
                        <span className="voice-icon">{'\u{1F469}'}</span>
                        <span className="voice-label">Female</span>
                    </div>
                    <div
                        className={`voice-option${selectedVoice === 'male' ? ' selected' : ''}`}
                        id="voice-male"
                        onClick={() => dispatch(setVoice('male'))}
                    >
                        <span className="voice-icon">{'\u{1F468}'}</span>
                        <span className="voice-label">Male</span>
                    </div>
                </div>
            </div>

            <div className="panel-section">
                <div className="panel-title">Options</div>
                <div className="toggle-row">
                    <span className="toggle-label">Edit script</span>
                    <button
                        className="btn btn-secondary btn-small"
                        id="edit-from-sidebar-btn"
                        onClick={() => onNavigate('edit')}
                    >
                        Edit
                    </button>
                </div>
                <div className="toggle-row">
                    <span className="toggle-label">Hide your lines</span>
                    <div
                        className={`toggle-switch${hideMyLines ? ' active' : ''}`}
                        id="toggle-hide-lines"
                        onClick={() => dispatch(toggleHideMyLines())}
                    ></div>
                </div>
                <div className="toggle-row">
                    <span className="toggle-label">Cue Mode</span>
                    <div
                        className={`toggle-switch${cueMode ? ' active' : ''}`}
                        id="toggle-cue-mode"
                        onClick={() => dispatch(toggleCueMode())}
                    ></div>
                </div>
                <div className="toggle-row">
                    <span className="toggle-label">Remove stage directions</span>
                    <div
                        className={`toggle-switch${removeStageDirections ? ' active' : ''}`}
                        id="toggle-stage"
                        onClick={() => dispatch(toggleStageDirections())}
                    ></div>
                </div>
                <div className="toggle-row">
                    <span className="toggle-label">
                        Microphone {micPermission === 'denied' && '(Denied)'}
                    </span>
                    <div
                        className={`toggle-switch${micEnabled ? ' active' : ''}`}
                        id="toggle-mic"
                        onClick={() => dispatch(toggleMic())}
                    ></div>
                </div>
                {micPermission === 'denied' && (
                    <p className="mic-permission-warning">
                        {permissionInstructions}
                    </p>
                )}
            </div>

            <div className="panel-section is-hidden" aria-hidden={true}>
                <div className="panel-title">Recordings</div>
                <div className="btn-row" style={{ justifyContent: 'flex-start' }}>
                    <button
                        className="btn btn-primary btn-small"
                        id="record-reader-btn"
                        onClick={() => onNavigate('readthrough')}
                    >
                        Record reader lines
                    </button>
                    <button
                        className="btn btn-secondary btn-small"
                        id="record-from-current-btn"
                        onClick={() => onNavigate('readthrough')}
                    >
                        Record from current line
                    </button>
                </div>
            </div>

            {problemCount > 0 && (
                <div className="panel-section problem-lines-section" id="problem-lines-section">
                    <div className="panel-title">
                        Problem Lines (<span id="problem-count">{problemCount}</span>)
                    </div>
                    <div id="problem-lines-list">
                        {problemIndices.slice(0, 5).map(index => {
                            const workingLines = getWorkingLines();
                            const line = workingLines[Number(index)];
                            if (!line) return null;
                            const missCount = problemLines[Number(index)];
                            return (
                                <div key={index} className="problem-line-item">
                                    <div className="problem-line-text">
                                        &ldquo;{line.dialogue.substring(0, 60)}{line.dialogue.length > 60 ? '...' : ''}&rdquo;
                                    </div>
                                    <div className="problem-line-count">
                                        Missed {missCount} time{missCount > 1 ? 's' : ''}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <button
                        className="btn btn-secondary btn-small"
                        id="drill-problems-btn"
                        style={{ marginTop: '12px', width: '100%' }}
                        onClick={handleDrillProblemLines}
                    >
                        Drill Problem Lines
                    </button>
                </div>
            )}
        </div>
    );
}

export default OptionsMenu;
